import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL, ENDPOINTS } from '../../../constants/api';
import { getToken } from '../../../utils/TokenUtils';
import '../css/BrowseTab.css';

function BrowseTab({ table }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('ASC');
  const [selectedRows, setSelectedRows] = useState([]);

  const token = getToken();

  useEffect(() => {
    const fetchBrowseData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_URL}/${ENDPOINTS.SQL}/browse/${table}`,
          {
            headers: { Authorization: token ? `Bearer ${token}` : '' },
          }
        );
        setRows(response.data || []);
      } catch (err) {
        console.error('Error fetching table data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (table) fetchBrowseData();
  }, [table]);

  const handleSort = (col) => {
    const isAsc = sortColumn === col && sortDirection === 'ASC';
    const direction = isAsc ? 'DESC' : 'ASC';
    setSortColumn(col);
    setSortDirection(direction);

    const sortedRows = [...rows].sort((a, b) => {
      if (a[col] === null) return 1;
      if (b[col] === null) return -1;
      if (a[col] < b[col]) return direction === 'ASC' ? -1 : 1;
      if (a[col] > b[col]) return direction === 'ASC' ? 1 : -1;
      return 0;
    });

    setRows(sortedRows);
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(rows.map((_, index) => index));
    } else {
      setSelectedRows([]);
    }
  };

  const toggleSelectRow = (index) => {
    setSelectedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  if (loading) {
    return <div className="p-3 text-muted">Loading table data...</div>;
  }

  if (!rows || !rows.length) {
    return (
      <div className="pma-alert-info">
        MySQL returned an empty result set (i.e. zero rows) for table{' '}
        <strong>{table}</strong>.
      </div>
    );
  }

  const columns = Object.keys(rows[0]);

  return (
    <div className="pma-browse-wrapper">
      <style>{`
        .pma-browse-wrapper {
          font-family: sans-serif;
          font-size: 12px;
        }
        .pma-sql-query-banner {
          background-color: #e2f0d9;
          border: 1px solid #b2d8a0;
          color: #2e6818;
          padding: 6px 10px;
          border-radius: 3px;
          font-family: monospace;
          margin-bottom: 12px;
        }
        .pma-grid-table {
          width: 100%;
          border-collapse: collapse;
          border: 1px solid #d0d0d0;
          background: #ffffff;
        }
        .pma-grid-table th {
          background: linear-gradient(to bottom, #f2f2f2, #e5e5e5);
          border: 1px solid #ccc;
          padding: 5px 8px;
          font-weight: bold;
          color: #333;
          white-space: nowrap;
        }
        .pma-grid-table th.sortable {
          cursor: pointer;
        }
        .pma-grid-table th.sortable:hover {
          background: #e0e0e0;
        }
        .pma-grid-table td {
          border: 1px solid #e0e0e0;
          padding: 4px 8px;
          font-family: monospace;
          white-space: nowrap;
          max-width: 250px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .pma-grid-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .pma-grid-table tr.selected-row {
          background-color: #fff8c4 !important;
        }
        .pma-grid-table tr:hover {
          background-color: #ffffcc;
        }
        .pma-action-cell {
          font-family: sans-serif;
          font-size: 11px;
          white-space: nowrap;
          text-align: center;
        }
        .pma-action-link {
          color: #23527c;
          text-decoration: none;
          margin: 0 3px;
          cursor: pointer;
        }
        .pma-action-link:hover {
          text-decoration: underline;
        }
        .pma-action-danger {
          color: #a94442;
        }
        .pma-null-tag {
          color: #888;
          font-style: italic;
          background: #eee;
          padding: 1px 4px;
          border-radius: 2px;
        }
        .pma-bottom-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #f5f5f5;
          border: 1px solid #ccc;
          border-top: none;
          padding: 6px 10px;
          border-radius: 0 0 3px 3px;
        }
      `}</style>

      {/* phpMyAdmin Query Execution Banner */}
      <div className="pma-sql-query-banner">
        <span>
          SELECT * FROM `{table}` LIMIT 0, {rows.length};
        </span>
      </div>

      {/* Main Grid Table */}
      <div className="table-responsive">
        <table className="pma-grid-table">
          <thead>
            <tr>
              <th style={{ width: '30px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  onChange={toggleSelectAll}
                  checked={
                    selectedRows.length === rows.length && rows.length > 0
                  }
                />
              </th>
              <th style={{ textAlign: 'center', width: '110px' }}>Action</th>
              {columns.map((col) => (
                <th
                  key={col}
                  className="sortable"
                  onClick={() => handleSort(col)}
                >
                  {col}{' '}
                  {sortColumn === col
                    ? sortDirection === 'ASC'
                      ? '▲'
                      : '▼'
                    : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const isSelected = selectedRows.includes(rowIndex);
              return (
                <tr key={rowIndex} className={isSelected ? 'selected-row' : ''}>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelectRow(rowIndex)}
                    />
                  </td>
                  <td className="pma-action-cell">
                    <span className="pma-action-link">Edit</span> |{' '}
                    <span className="pma-action-link">Copy</span> |{' '}
                    <span className="pma-action-link pma-action-danger">
                      Delete
                    </span>
                  </td>
                  {columns.map((col, colIndex) => {
                    const val = row[col];
                    return (
                      <td
                        key={colIndex}
                        title={val !== null ? String(val) : 'NULL'}
                      >
                        {val !== null && val !== undefined ? (
                          String(val)
                        ) : (
                          <span className="pma-null-tag">NULL</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Bottom Selection Toolbar */}
      <div className="pma-bottom-toolbar">
        <div>
          <span className="text-muted me-2">With selected:</span>
          <button
            className="btn btn-outline-secondary btn-sm py-0 px-2 me-1"
            style={{ fontSize: '11px' }}
          >
            Export
          </button>
          <button
            className="btn btn-outline-danger btn-sm py-0 px-2"
            style={{ fontSize: '11px' }}
          >
            Delete
          </button>
        </div>
        <div className="text-muted">
          Showing 1 - {rows.length} rows ({rows.length} total)
        </div>
      </div>
    </div>
  );
}

export default BrowseTab;
